import {
  type Except,
  notEmpty,
  objectUtils,
  type Optional,
  ReactUtils,
} from '@festina-life/core'
import cx from 'classnames'
import { type JSX, type MouseEvent, useEffect, useMemo, useRef } from 'react'

import { NavLink } from '../../actions/navLink/NavLink'
import { EmptyState } from '../../feedback/emptyState/EmptyState'
import { useTranslate } from '../../hooks/useTranslate'
import {
  Table,
  type TableProps,
  TBody,
  Td,
  TFoot,
  Th,
  THead,
  Tr,
} from '../table/Table'
import type { RowAction } from '../types'
import type { Column, ColumnId } from './createColumnDefiner'
import { SortableTableDataCell } from './SortableTableDataCell'
import { SortableTableHeader } from './SortableTableHeader'
import { SortableTableRowActionButton } from './SortableTableRowActionButton'
import { useFilterState } from './useFilterState'
import { useSortingState } from './useSortingState'

const rightStickyCellStyles = cx(
  'sticky [[data-amaze-cardbody]>table_&]:-right-1.5 right-0 z-1',
  'before:content-[""] before:absolute before:-left-px before:w-px before:h-full before:top-0 before:z-2 before:bg-slate-200 dark:before:bg-slate-700',
)

type Props<T> = {
  data: Optional<T>[]
  footerData?: T[]
  columns: Record<ColumnId, Column<T>>
  searchCriteria?: string
  rowOnClick?: (x: T, e: MouseEvent<HTMLTableRowElement>) => void
  rowTo?: (x: T) => string | undefined
  rowIsSelected?: (x: T) => boolean
  rowActions?: (x: T) => RowAction[]
  rowClassName?: (x: T) => JSX.IntrinsicElements['tr']['className']
  onPresentDataChange?: (data: T[]) => void
  emptyOptions?: {
    itemName?: string
    reason?: string
  }
} & Except<TableProps, 'title' | 'children'>

export function SortableTable<T extends WeakKey>({
  searchCriteria,
  data,
  footerData,
  columns,
  rowOnClick,
  rowTo,
  rowIsSelected,
  rowActions,
  rowClassName,
  onPresentDataChange,
  emptyOptions,
  ...restProps
}: Props<T>) {
  const translate = useTranslate()

  const rowKey = useRef(objectUtils.createReactKeyGetterFromObjectIdentity())

  const { getSortedRows, getSortingState, cycleColumnSorting } =
    useSortingState<T>({ columns })

  const { getFilteredRows, getFilterState, setFilterState } = useFilterState<T>(
    {
      columns,
      rows: data,
      globalSearch: searchCriteria ?? '',
    },
  )

  const rows: T[] = useMemo(
    () =>
      // Following https://tanstack.com/table/latest/docs/guide/row-models#the-order-of-row-model-execution,
      // filter-then-sort
      getSortedRows(getFilteredRows(data.filter(notEmpty))),
    [getSortedRows, getFilteredRows, data],
  )

  useEffect(() => {
    onPresentDataChange?.(rows)
  }, [rows, onPresentDataChange])

  return (
    <Table {...restProps}>
      {rows.length === 0 && (
        <caption className="caption-bottom">
          <EmptyState
            itemName={emptyOptions?.itemName}
            reason={emptyOptions?.reason}
            variant="tableCaption"
          />
        </caption>
      )}
      <THead>
        <Tr>
          {Object.entries(columns).map(([columnId, column]) => (
            <SortableTableHeader
              key={columnId}
              column={column}
              sortingState={getSortingState(columnId)}
              filterState={getFilterState(columnId)}
              onClick={
                column.sortable !== false
                  ? (e) =>
                      cycleColumnSorting({
                        columnId,
                        // metaKey for command key on Mac https://stackoverflow.com/a/3922353
                        isAdditive: e.ctrlKey || e.metaKey,
                      })
                  : undefined
              }
              onStateChanged={(state) => setFilterState(columnId, state)}
            />
          ))}
          {rowActions && (
            <Th className={rightStickyCellStyles}>{translate('actions')}</Th>
          )}
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => {
          const to = rowTo?.(row)
          return (
            <ReactUtils.ConditionalWrap
              key={rowKey.current(row)}
              condition={!!to}
              wrap={(children) => (
                // We are wrapping in a navlink to get the link behaviour but we don't want it to impact table layout so we effectively hide it at the same time
                // Styles ensuring it looks like something you can click on hover is handled by the Tr element below
                <NavLink className="contents! before:hidden!" to={to!}>
                  {children}
                </NavLink>
              )}
            >
              <Tr
                {...(rowOnClick && {
                  role: 'button',
                  onClick: (e) => rowOnClick(row, e),
                })}
                isRowClickable={!!rowOnClick || typeof to === 'string'}
                isRowSelected={rowIsSelected?.(row)}
                className={rowClassName?.(row)}
              >
                {Object.entries(columns).map(([columnId, column]) => (
                  <SortableTableDataCell
                    key={columnId}
                    column={column}
                    row={row}
                  />
                ))}
                {rowActions && (
                  <Td
                    className={cx(
                      `w-0 whitespace-nowrap p-0!`,
                      rightStickyCellStyles,
                    )}
                  >
                    {rowActions(row).map(({ key, ...rowAction }) => (
                      <SortableTableRowActionButton
                        key={key}
                        rowAction={rowAction}
                      />
                    ))}
                  </Td>
                )}
              </Tr>
            </ReactUtils.ConditionalWrap>
          )
        })}
      </TBody>
      {footerData && (
        <TFoot>
          {footerData.map((row) => (
            <Tr key={rowKey.current(row)}>
              {Object.entries(columns).map(([columnId, column]) => (
                <SortableTableDataCell
                  key={columnId}
                  column={column}
                  row={row}
                  isHeader
                />
              ))}
              {rowActions && <Th />}
            </Tr>
          ))}
        </TFoot>
      )}
    </Table>
  )
}
